/**
 * Small, dependency-free math helpers used by the frond engine.
 *
 * All coordinates are float64. The engine's coordinate system is a tree of
 * "cells" (see cell.ts) where every level halves the scale, so absolute
 * precision is preserved at any depth: composing N levels multiplies by
 * exact powers of two, which are representable without error in float64.
 */

export interface Vec2 {
  x: number;
  y: number;
}

/** Affine 2D transform, canvas-style matrix:
 *  x' = a*x + c*y + e
 *  y' = b*x + d*y + f
 * (Same convention as CanvasRenderingContext2D.setTransform.) */
export interface Mat2D {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export const IDENTITY: Mat2D = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };

export const v = (x: number, y: number): Vec2 => ({ x, y });

export const add = (p: Vec2, q: Vec2): Vec2 => ({ x: p.x + q.x, y: p.y + q.y });
export const sub = (p: Vec2, q: Vec2): Vec2 => ({ x: p.x - q.x, y: p.y - q.y });
export const scale = (p: Vec2, k: number): Vec2 => ({ x: p.x * k, y: p.y * k });
export const dot = (p: Vec2, q: Vec2): number => p.x * q.x + p.y * q.y;
export const len = (p: Vec2): number => Math.hypot(p.x, p.y);
export const dist = (p: Vec2, q: Vec2): number => Math.hypot(p.x - q.x, p.y - q.y);
export const norm = (p: Vec2): Vec2 => {
  const l = len(p);
  return l < 1e-12 ? { x: 1, y: 0 } : { x: p.x / l, y: p.y / l };
};
export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
export const clamp = (x: number, lo: number, hi: number): number =>
  x < lo ? lo : x > hi ? hi : x;
export const vLerp = (p: Vec2, q: Vec2, t: number): Vec2 => ({
  x: lerp(p.x, q.x, t),
  y: lerp(p.y, q.y, t),
});

/* ------------------------------ transforms ------------------------------ */

export const translate = (tx: number, ty: number): Mat2D => ({
  a: 1,
  b: 0,
  c: 0,
  d: 1,
  e: tx,
  f: ty,
});

export const scaleM = (s: number): Mat2D => ({ a: s, b: 0, c: 0, d: s, e: 0, f: 0 });

/** Rotation in canvas coordinates (y-down): positive = clockwise on screen. */
export const rotateM = (theta: number): Mat2D => {
  const c = Math.cos(theta);
  const s = Math.sin(theta);
  return { a: c, b: s, c: -s, d: c, e: 0, f: 0 };
};

/** Compose: result = A ∘ B (apply B first, then A). */
export const mul = (A: Mat2D, B: Mat2D): Mat2D => ({
  a: A.a * B.a + A.c * B.b,
  b: A.b * B.a + A.d * B.b,
  c: A.a * B.c + A.c * B.d,
  d: A.b * B.c + A.d * B.d,
  e: A.a * B.e + A.c * B.f + A.e,
  f: A.b * B.e + A.d * B.f + A.f,
});

export const inv = (m: Mat2D): Mat2D => {
  const det = m.a * m.d - m.b * m.c;
  if (Math.abs(det) < 1e-15) return IDENTITY;
  const ia = m.d / det;
  const ib = -m.b / det;
  const ic = -m.c / det;
  const id = m.a / det;
  return {
    a: ia,
    b: ib,
    c: ic,
    d: id,
    e: -(ia * m.e + ic * m.f),
    f: -(ib * m.e + id * m.f),
  };
};

export const apply = (m: Mat2D, p: Vec2): Vec2 => ({
  x: m.a * p.x + m.c * p.y + m.e,
  y: m.b * p.x + m.d * p.y + m.f,
});

/** Apply only the linear part (no translation). */
export const applyLin = (m: Mat2D, p: Vec2): Vec2 => ({
  x: m.a * p.x + m.c * p.y,
  y: m.b * p.x + m.d * p.y,
});

export const det = (m: Mat2D): number => m.a * m.d - m.b * m.c;

export const scaleOf = (m: Mat2D): number => Math.sqrt(Math.abs(det(m)));

/** Approximate error scale for tolerance computations. */
export const tolOf = (m: Mat2D): number => {
  const s = scaleOf(m);
  return s > 0 ? 1 / s : 1;
};

/* ------------------------------- geometry ------------------------------- */

export const rectsIntersect = (a: Rect, b: Rect): boolean =>
  a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

export const rectContains = (r: Rect, p: Vec2): boolean =>
  p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h;

/** Transform a rect by an affine transform (returns axis-aligned bounds). */
export const transformRect = (m: Mat2D, r: Rect): Rect => {
  const c0 = apply(m, { x: r.x, y: r.y });
  const c1 = apply(m, { x: r.x + r.w, y: r.y });
  const c2 = apply(m, { x: r.x, y: r.y + r.h });
  const c3 = apply(m, { x: r.x + r.w, y: r.y + r.h });
  const xs = [c0.x, c1.x, c2.x, c3.x];
  const ys = [c0.y, c1.y, c2.y, c3.y];
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
};

export const unionRect = (a: Rect, b: Rect): Rect => {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return {
    x,
    y,
    w: Math.max(a.x + a.w, b.x + b.w) - x,
    h: Math.max(a.y + a.h, b.y + b.h) - y,
  };
};
